// src/pages/JudgmentDetail.js
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiRequest } from "../api";

export default function JudgmentDetail() {
  const { jid } = useParams();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // new note draft
  const [noteText, setNoteText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState(null);

  // new payment draft
  const [payAmount, setPayAmount] = useState("");
  const [payDate, setPayDate] = useState(() => new Date().toISOString().slice(0, 10)); // YYYY-MM-DD
  const [payMethod, setPayMethod] = useState("CASH");
  const [payRef, setPayRef] = useState("");
  const [payDesc, setPayDesc] = useState("");
  const [payRate, setPayRate] = useState("0.35");
  const [paySubmitting, setPaySubmitting] = useState(false);
  const [payErr, setPayErr] = useState(null);

  // Dunn UI state
  const [dunnLoading, setDunnLoading] = useState(false);
  const [dunnErr, setDunnErr] = useState(null);
  const [dunnNoticeCode, setDunnNoticeCode] = useState("JUD_N1");
  const [dunnPreview, setDunnPreview] = useState(null);
  const [dunnEvents, setDunnEvents] = useState([]);
  const [dunnEventsLoading, setDunnEventsLoading] = useState(false);
  const [dunnActionBusyId, setDunnActionBusyId] = useState(null);

  // For testing/error marking from UI (optional)
  const [markSentVia, setMarkSentVia] = useState("manual");
  const [markErrCode, setMarkErrCode] = useState("test_failure");
  const [markErrMsg, setMarkErrMsg] = useState("testing_mark_error");

  async function loadDetail() {
    try {
      setLoading(true);
      setErr(null);
      const data = await apiRequest(`/judgments/${jid}`);
      setDetail(data);
    } catch (e) {
      console.error(e);
      setErr(e.message || "Failed to load judgment");
    } finally {
      setLoading(false);
    }
  }

  async function loadDunnEvents() {
    try {
      setDunnEventsLoading(true);
      // entity_type for your dunn system is "judgment", entity_id is jid
      const data = await apiRequest(
        `/dunn/events?entity_type=judgment&entity_id=${encodeURIComponent(jid)}`
      );
      setDunnEvents(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      console.error(e);
      // keep page usable even if dunn list fails
      setDunnEvents([]);
    } finally {
      setDunnEventsLoading(false);
    }
  }

  useEffect(() => {
    loadDetail();
    loadDunnEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jid]);

  async function handleAddNote(e) {
    e.preventDefault();
    if (!noteText.trim()) return;

    try {
      setSubmitting(true);
      setSubmitErr(null);

      await apiRequest(`/judgments/${jid}/notes`, {
        method: "POST",
        body: {
          note: noteText.trim(),
          pinned: false,
        },
      });

      await loadDetail();
      setNoteText("");
    } catch (e) {
      console.error(e);
      setSubmitErr(e.message || "Failed to add note");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAddPayment(e) {
    e.preventDefault();

    const amt = Number(payAmount);
    if (!amt || amt <= 0) {
      setPayErr("Enter a valid payment amount.");
      return;
    }

    try {
      setPaySubmitting(true);
      setPayErr(null);

      await apiRequest(`/judgments/${jid}/payments`, {
        method: "POST",
        body: {
          amount: String(payAmount),
          occurred_at: payDate,
          method: payMethod || null,
          reference: payRef || null,
          description: payDesc || null,
          contingency_rate: payRate === "" ? null : String(payRate),
        },
      });

      await loadDetail();

      // reset some fields
      setPayAmount("");
      setPayRef("");
      setPayDesc("");
    } catch (e) {
      console.error(e);
      setPayErr(e.message || "Failed to add payment");
    } finally {
      setPaySubmitting(false);
    }
  }

  async function handleAddRefund(e) {
    e.preventDefault();

    const amt = Number(payAmount);
    if (!amt || amt <= 0) {
      setPayErr("Enter a valid payment amount.");
      return;
    }

    try {
      setPaySubmitting(true);
      setPayErr(null);

      await apiRequest(`/judgments/${jid}/refunds`, {
        method: "POST",
        body: {
          amount: String(payAmount),
          occurred_at: payDate,
          method: payMethod || null,
          reference: payRef || null,
          description: payDesc || null,
          contingency_rate: payRate === "" ? null : String(payRate),
        },
      });

      await loadDetail();

      // reset some fields
      setPayAmount("");
      setPayRef("");
      setPayDesc("");
    } catch (e) {
      console.error(e);
      setPayErr(e.message || "Failed to issue refund");
    } finally {
      setPaySubmitting(false);
    }
  }

  // ------------------------
  // Dunn handlers
  // ------------------------
  async function dunnPreviewNow() {
    try {
      setDunnLoading(true);
      setDunnErr(null);
      setDunnPreview(null);

      const qs =
        `module=judgment` +
        `&entity_type=judgment` +
        `&entity_id=${encodeURIComponent(jid)}` +
        `&notice_code=${encodeURIComponent(dunnNoticeCode || "JUD_N1")}`;

      const data = await apiRequest(`/dunn/preview?${qs}`, { method: "POST" });
      setDunnPreview(data || null);
    } catch (e) {
      console.error(e);
      setDunnErr(e.message || "Failed to preview Dunn notice");
    } finally {
      setDunnLoading(false);
    }
  }

  async function dunnGenerateNow() {
    try {
      setDunnLoading(true);
      setDunnErr(null);

      const qs =
        `module=judgment` +
        `&entity_type=judgment` +
        `&entity_id=${encodeURIComponent(jid)}` +
        `&notice_code=${encodeURIComponent(dunnNoticeCode || "JUD_N1")}`;

      const data = await apiRequest(`/dunn/generate?${qs}`, { method: "POST" });

      // refresh list + show last response
      setDunnPreview(data || null);
      await loadDunnEvents();
    } catch (e) {
      console.error(e);
      setDunnErr(e.message || "Failed to generate Dunn notice");
    } finally {
      setDunnLoading(false);
    }
  }

  async function dunnMarkSent(eventId) {
    try {
      setDunnActionBusyId(eventId);
      setDunnErr(null);

      const qs = `dunn_event_id=${encodeURIComponent(eventId)}&sent_via=${encodeURIComponent(
        markSentVia || "manual"
      )}`;

      await apiRequest(`/dunn/mark-sent?${qs}`, { method: "POST" });
      await loadDunnEvents();
    } catch (e) {
      console.error(e);
      setDunnErr(e.message || "Failed to mark sent");
    } finally {
      setDunnActionBusyId(null);
    }
  }

  async function dunnMarkError(eventId) {
    try {
      setDunnActionBusyId(eventId);
      setDunnErr(null);

      const qs =
        `dunn_event_id=${encodeURIComponent(eventId)}` +
        `&error_code=${encodeURIComponent(markErrCode || "error")}` +
        `&error_message=${encodeURIComponent(markErrMsg || "error")}`;

      await apiRequest(`/dunn/mark-error?${qs}`, { method: "POST" });
      await loadDunnEvents();
    } catch (e) {
      console.error(e);
      setDunnErr(e.message || "Failed to mark error");
    } finally {
      setDunnActionBusyId(null);
    }
  }

  async function dunnResetStatus(eventId, newStatus, clearError) {
    try {
      setDunnActionBusyId(eventId);
      setDunnErr(null);

      const qs =
        `dunn_event_id=${encodeURIComponent(eventId)}` +
        `&new_status=${encodeURIComponent(newStatus)}` +
        `&clear_error=${clearError ? "true" : "false"}`;

      await apiRequest(`/dunn/reset-status?${qs}`, { method: "POST" });
      await loadDunnEvents();
    } catch (e) {
      console.error(e);
      setDunnErr(e.message || "Failed to reset status");
    } finally {
      setDunnActionBusyId(null);
    }
  }

// Always define safe default
const dunnLatest = React.useMemo(() => {
  const list = Array.isArray(dunnEvents) ? dunnEvents : [];
  if (list.length === 0) return null;

  const sorted = [...list].sort((a, b) => {
    const da = new Date(a.created_at || 0).getTime();
    const db = new Date(b.created_at || 0).getTime();
    return db - da;
  });

  return sorted[0];
}, [dunnEvents]);

  // ------------------------
  // Render guards
  // ------------------------
  if (loading) {
    return <div style={{ padding: 24 }}>Loading judgment #{jid}…</div>;
  }
  if (err) {
    return (
      <div style={{ padding: 24, color: "red" }}>
        Error loading judgment #{jid}: {err}
      </div>
    );
  }
  if (!detail) {
    return <div style={{ padding: 24 }}>No data for judgment #{jid}</div>;
  }

  // unpack pieces (what your backend returns from /api/judgments/{jid})
  const { judgment, parties, balance, notes, transactions } = detail;

  // Balance display: show Due + Credit (credit when overpaid)
  const rawDue = balance?.balance_due != null ? Number(balance.balance_due) : null;
  const due = rawDue == null ? null : Math.max(0, rawDue);
  const credit = rawDue == null ? null : Math.max(0, -rawDue);

  // normalize plaintiff/defendant lists for quick reading
  const plaintiffs = (parties || []).filter((p) => p.role === "PLAINTIFF");
  const defendants = (parties || []).filter((p) => p.role === "DEFENDANT");

  // ledger math
  const txs = (transactions || []).slice(); // API returns newest-first (fine)
  const sumByKind = (kind) =>
    txs.filter((t) => t.kind === kind).reduce((acc, t) => acc + Number(t.amount || 0), 0);

  const totalPayments = sumByKind("PAYMENT");
  const totalFees = sumByKind("FEE");
  const totalCosts = sumByKind("COST");
  const netToClient = totalPayments - totalFees; // simple net (Payments - Fees)

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", paddingBottom: 80 }}>
      {/* HEADER CARD */}
      <section style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
          <div style={{ minWidth: 250 }}>
            <div style={miniLabel}>Case Number</div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{judgment?.case_number || "—"}</div>

            <div style={{ marginTop: 12, ...miniLabel }}>Court</div>
            <div style={{ fontSize: 14 }}>
              {judgment?.court || "—"}
              {judgment?.county ? ` (${judgment.county})` : ""}
            </div>

            <div style={{ marginTop: 12, ...miniLabel }}>Status</div>
            <div style={statusPill}>{judgment?.status || "—"}</div>
          </div>

          <div style={{ minWidth: 250, marginTop: 12 }}>
            <div style={miniLabel}>Principal</div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>
              {judgment?.principal != null ? `$${Number(judgment.principal).toFixed(2)}` : "—"}
            </div>

            <div style={{ marginTop: 12, ...miniLabel }}>Judgment Date</div>
            <div style={{ fontSize: 14 }}>{judgment?.judgment_date || "—"}</div>

            <div style={{ marginTop: 12, ...miniLabel }}>Interest Rate</div>
            <div style={{ fontSize: 14 }}>
              {judgment?.interest_rate != null ? `${Number(judgment.interest_rate) * 100}% simple` : "—"}
            </div>
          </div>

          <div style={{ minWidth: 250, marginTop: 12 }}>
            <div style={miniLabel}>Current Balance Due</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#b00020" }}>
              {due != null ? `$${due.toFixed(2)}` : "—"}
            </div>

            {credit != null && credit > 0 && (
              <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                Credit: ${credit.toFixed(2)}
              </div>
            )}

            {balance && (
              <div style={{ fontSize: 12, color: "#666", marginTop: 4, lineHeight: 1.4 }}>
                Accrued interest: ${Number(balance.interest_accrued || 0).toFixed(2)}
                <br />
                Payments received: ${Number(balance.total_payments || 0).toFixed(2)}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* PARTIES CARD */}
      <section style={cardStyle}>
        <h2 style={sectionTitleStyle}>Parties</h2>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 24 }}>
          <div style={{ minWidth: 260 }}>
            <div style={pillHeader}>Plaintiff(s)</div>
            {plaintiffs.length === 0 && <div style={emptyMini}>No plaintiffs yet.</div>}
            {plaintiffs.map((p) => (
              <PartyCard key={p.id} party={p} />
            ))}
          </div>

          <div style={{ minWidth: 260 }}>
            <div style={pillHeader}>Defendant(s)</div>
            {defendants.length === 0 && <div style={emptyMini}>No defendants yet.</div>}
            {defendants.map((p) => (
              <PartyCard key={p.id} party={p} />
            ))}
          </div>
        </div>
      </section>

      {/* DUNN NOTICES CARD */}
      <section style={cardStyle}>
        <h2 style={sectionTitleStyle}>Dunn Notices</h2>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "end" }}>
          <div>
            <div style={miniLabel}>Notice Code</div>
            <input
              value={dunnNoticeCode}
              onChange={(e) => setDunnNoticeCode(e.target.value)}
              placeholder="JUD_N1"
              style={inputStyle}
              disabled={dunnLoading}
            />
          </div>

          <button
            type="button"
            onClick={dunnPreviewNow}
            disabled={dunnLoading}
            style={{ ...btnPrimary, background: "#1976d2", borderColor: "#115293" }}
          >
            {dunnLoading ? "Working…" : "Preview"}
          </button>

          <button
            type="button"
            onClick={dunnGenerateNow}
            disabled={dunnLoading}
            style={{ ...btnPrimary, background: "#2e7d32", borderColor: "#1b5e20" }}
          >
            {dunnLoading ? "Working…" : "Generate"}
          </button>

          <button
            type="button"
            onClick={loadDunnEvents}
            disabled={dunnEventsLoading}
            style={btnSecondary}
          >
            {dunnEventsLoading ? "Refreshing…" : "Refresh Events"}
          </button>

          {dunnLatest && (
            <div style={{ fontSize: 12, color: "#555", marginLeft: 6 }}>
              Latest:{" "}
              <span style={{ fontWeight: 700 }}>
                #{dunnLatest.id} · {dunnLatest.notice_code} · {dunnLatest.status}
              </span>
            </div>
          )}
        </div>

        {dunnErr && <div style={{ color: "red", fontSize: 12, marginTop: 10 }}>{dunnErr}</div>}

        {/* Preview / Generate response */}
        {dunnPreview && (
          <div style={{ marginTop: 14, border: "1px solid #eee", borderRadius: 8, padding: 12, background: "#fafafa" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontSize: 13 }}>
                <div style={{ fontWeight: 800, marginBottom: 6 }}>
                  {dunnPreview.generated ? "Generated" : "Preview"} Result
                </div>
                <div>template_id: <b>{dunnPreview.template_id || "—"}</b></div>
                <div>template_ver: <b>{dunnPreview.template_ver || "—"}</b></div>
                <div>dunn_code: <b>{dunnPreview.dunn_code || "—"}</b></div>
                {dunnPreview.dunn_event_id != null && (
                  <div>dunn_event_id: <b>{dunnPreview.dunn_event_id}</b></div>
                )}
              </div>

              <div style={{ fontSize: 12, color: "#444", minWidth: 320, flex: "1 1 320px" }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>payload</div>
                <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                  {JSON.stringify(dunnPreview.payload || {}, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        )}

        {/* Events list */}
        <div style={{ marginTop: 14 }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "end" }}>
            <div>
              <div style={miniLabel}>Mark sent via</div>
              <input
                value={markSentVia}
                onChange={(e) => setMarkSentVia(e.target.value)}
                placeholder="manual"
                style={{ ...inputStyle, width: 140 }}
              />
            </div>
            <div>
              <div style={miniLabel}>Error code</div>
              <input
                value={markErrCode}
                onChange={(e) => setMarkErrCode(e.target.value)}
                placeholder="test_failure"
                style={{ ...inputStyle, width: 160 }}
              />
            </div>
            <div style={{ flex: "1 1 280px" }}>
              <div style={miniLabel}>Error message</div>
              <input
                value={markErrMsg}
                onChange={(e) => setMarkErrMsg(e.target.value)}
                placeholder="testing_mark_error"
                style={{ ...inputStyle, width: "100%" }}
              />
            </div>
          </div>

          {dunnEventsLoading ? (
            <div style={{ marginTop: 10, fontSize: 12, color: "#666" }}>Loading events…</div>
          ) : dunnEvents.length === 0 ? (
            <div style={{ marginTop: 10, ...emptyMini }}>No Dunn events yet for this judgment.</div>
          ) : (
            <div style={{ overflowX: "auto", marginTop: 10 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={thStyle}>ID</th>
                    <th style={thStyle}>Created</th>
                    <th style={thStyle}>Notice</th>
                    <th style={thStyle}>Template</th>
                    <th style={thStyle}>Code</th>
                    <th style={thStyle}>Status</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {dunnEvents.map((ev) => {
                    const busy = dunnActionBusyId === ev.id;
                    return (
                      <tr key={ev.id}>
                        <td style={tdStyle}>{ev.id}</td>
                        <td style={tdStyle}>{ev.created_at ? formatDateTime(ev.created_at) : "—"}</td>
                        <td style={tdStyle}>{ev.notice_code || "—"}</td>
                        <td style={tdStyle}>
                          {(ev.template_id || "—")}{ev.template_ver ? ` (${ev.template_ver})` : ""}
                        </td>
                        <td style={tdStyle} title={ev.dunn_code || ""}>
                          <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                            {ev.dunn_code || "—"}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <span style={dunnStatusPill(ev.status)}>{ev.status || "—"}</span>
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => dunnMarkSent(ev.id)}
                            style={{ ...miniBtn, marginRight: 8 }}
                            title="Mark this notice as sent"
                          >
                            {busy ? "…" : "Mark Sent"}
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => dunnMarkError(ev.id)}
                            style={{ ...miniBtn, marginRight: 8 }}
                            title="Mark this notice as error"
                          >
                            {busy ? "…" : "Mark Error"}
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => dunnResetStatus(ev.id, "generated", true)}
                            style={miniBtn}
                            title="Reset to generated and clear error"
                          >
                            {busy ? "…" : "Reset"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* PAYMENTS CARD (Form) */}
      <section style={cardStyle}>
        <h2 style={sectionTitleStyle}>Payments</h2>

        <form onSubmit={handleAddPayment} style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={miniLabel}>Amount</div>
            <input
              value={payAmount}
              onChange={(e) => setPayAmount(e.target.value)}
              placeholder="250.00"
              style={inputStyle}
              disabled={paySubmitting}
            />
          </div>

          <div>
            <div style={miniLabel}>Date</div>
            <input
              type="date"
              value={payDate}
              onChange={(e) => setPayDate(e.target.value)}
              style={inputStyle}
              disabled={paySubmitting}
            />
          </div>

          <div>
            <div style={miniLabel}>Method</div>
            <select
              value={payMethod}
              onChange={(e) => setPayMethod(e.target.value)}
              style={inputStyle}
              disabled={paySubmitting}
            >
              <option value="CASH">CASH</option>
              <option value="CHECK">CHECK</option>
              <option value="ZELLE">ZELLE</option>
              <option value="ACH">ACH</option>
              <option value="CARD">CARD</option>
              <option value="OTHER">OTHER</option>
            </select>
          </div>

          <div>
            <div style={miniLabel}>Reference</div>
            <input
              value={payRef}
              onChange={(e) => setPayRef(e.target.value)}
              placeholder="R-001"
              style={inputStyle}
              disabled={paySubmitting}
            />
          </div>

          <div style={{ flex: "1 1 260px" }}>
            <div style={miniLabel}>Description</div>
            <input
              value={payDesc}
              onChange={(e) => setPayDesc(e.target.value)}
              placeholder="Test payment"
              style={{ ...inputStyle, width: "100%" }}
              disabled={paySubmitting}
            />
          </div>

          <div>
            <div style={miniLabel}>Contingency Rate</div>
            <input
              value={payRate}
              onChange={(e) => setPayRate(e.target.value)}
              placeholder="0.35"
              style={{ ...inputStyle, width: 110 }}
              disabled={paySubmitting}
            />
          </div>

          <div style={{ display: "flex", alignItems: "end" }}>
            <button type="submit" disabled={paySubmitting} style={btnPrimary}>
              {paySubmitting ? "Saving…" : "Add Payment"}
            </button>

            <button
              type="button"
              onClick={handleAddRefund}
              disabled={paySubmitting}
              style={{ ...btnSecondary, marginLeft: 10 }}
            >
              Issue Refund
            </button>
          </div>
        </form>

        {payErr && <div style={{ color: "red", fontSize: 12, marginTop: 8 }}>{payErr}</div>}
      </section>

      {/* LEDGER CARD */}
      <section style={cardStyle}>
        <h2 style={sectionTitleStyle}>Payments & Ledger</h2>

        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 12 }}>
          <div style={{ fontSize: 13 }}>
            <div style={{ color: "#666" }}>Payments</div>
            <div style={{ fontWeight: 700 }}>${totalPayments.toFixed(2)}</div>
          </div>
          <div style={{ fontSize: 13 }}>
            <div style={{ color: "#666" }}>Fees</div>
            <div style={{ fontWeight: 700 }}>${totalFees.toFixed(2)}</div>
          </div>
          <div style={{ fontSize: 13 }}>
            <div style={{ color: "#666" }}>Costs</div>
            <div style={{ fontWeight: 700 }}>${totalCosts.toFixed(2)}</div>
          </div>
          <div style={{ fontSize: 13 }}>
            <div style={{ color: "#666" }}>Net (Payments − Fees)</div>
            <div style={{ fontWeight: 800 }}>${netToClient.toFixed(2)}</div>
          </div>
        </div>

        {txs.length === 0 ? (
          <div style={emptyMini}>No transactions yet.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Method</th>
                  <th style={thStyle}>Reference</th>
                  <th style={thStyle}>Description</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {txs.map((t) => (
                  <tr key={t.id}>
                    <td style={tdStyle}>{t.occurred_at || "—"}</td>
                    <td style={tdStyle}>{t.kind || "—"}</td>
                    <td style={tdStyle}>{t.method || "—"}</td>
                    <td style={tdStyle}>{t.reference || "—"}</td>
                    <td style={tdStyle}>{t.description || "—"}</td>
                    <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700 }}>
                      ${Number(t.amount || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* NOTES CARD */}
      <section style={cardStyle}>
        <h2 style={sectionTitleStyle}>Activity / Notes</h2>

        <form onSubmit={handleAddNote} style={{ marginBottom: 16 }}>
          <textarea
            rows={3}
            placeholder="Add internal activity note (skip trace started, demand letter sent, promise to pay, etc.)"
            style={{
              width: "100%",
              fontSize: 13,
              padding: 8,
              border: "1px solid #ccc",
              borderRadius: 4,
              resize: "vertical",
              background: "#fff",
            }}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            disabled={submitting}
          />
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
            <button
              type="submit"
              disabled={submitting || !noteText.trim()}
              style={btnPrimary}
            >
              {submitting ? "Saving…" : "Save Note"}
            </button>

            {submitErr && <div style={{ color: "red", fontSize: 12 }}>{submitErr}</div>}
          </div>
        </form>

        {(!notes || notes.length === 0) && <div style={emptyMini}>No notes yet.</div>}

        <div style={{ display: "grid", gap: 12 }}>
          {(notes || []).map((n) => (
            <div
              key={n.id}
              style={{
                border: "1px solid #eee",
                borderRadius: 6,
                padding: 12,
                background: "#fafafa",
              }}
            >
              <div style={{ fontSize: 13, whiteSpace: "pre-wrap", lineHeight: 1.4 }}>
                {n.note}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "#666",
                  marginTop: 6,
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <span>by {n.author || "unknown"}</span>
                <span>{formatDateTime(n.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// helper subcomponent: shows one party cleanly
function PartyCard({ party }) {
  return (
    <div
      style={{
        border: "1px solid #eee",
        borderRadius: 6,
        padding: 10,
        fontSize: 13,
        background: "#fafafa",
        marginBottom: 10,
      }}
    >
      <div style={{ fontWeight: 600, color: "#222" }}>{party.name || "—"}</div>
      {party.address && <div style={{ color: "#444", marginTop: 4, lineHeight: 1.4 }}>{party.address}</div>}
      <div style={{ color: "#666", marginTop: 4, lineHeight: 1.4 }}>
        {party.phone ? `📞 ${party.phone}` : ""}
        {party.phone && party.email ? " · " : ""}
        {party.email ? `✉ ${party.email}` : ""}
      </div>
    </div>
  );
}

// util: quick nice timestamp
function formatDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

function dunnStatusPill(status) {
  const s = String(status || "").toLowerCase();
  if (s === "sent") {
    return {
      fontSize: 12,
      fontWeight: 700,
      padding: "2px 8px",
      borderRadius: 999,
      border: "1px solid #b7e1c1",
      background: "#e9f7ee",
      color: "#1b5e20",
      display: "inline-block",
    };
  }
  if (s === "error") {
    return {
      fontSize: 12,
      fontWeight: 700,
      padding: "2px 8px",
      borderRadius: 999,
      border: "1px solid #f3b7b7",
      background: "#fdecec",
      color: "#8a1c1c",
      display: "inline-block",
    };
  }
  if (s === "generated") {
    return {
      fontSize: 12,
      fontWeight: 700,
      padding: "2px 8px",
      borderRadius: 999,
      border: "1px solid #c7dbff",
      background: "#eef5ff",
      color: "#1a3c7a",
      display: "inline-block",
    };
  }
  return {
    fontSize: 12,
    fontWeight: 700,
    padding: "2px 8px",
    borderRadius: 999,
    border: "1px solid #ddd",
    background: "#f7f7f7",
    color: "#444",
    display: "inline-block",
  };
}

const cardStyle = {
  border: "1px solid #ddd",
  borderRadius: 8,
  padding: 16,
  marginTop: 16,
  background: "#fff",
  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
};

const miniLabel = { fontSize: 12, color: "#666", marginBottom: 4 };

const statusPill = {
  display: "inline-block",
  fontSize: 12,
  background: "#eef5ff",
  border: "1px solid #c7dbff",
  color: "#1a3c7a",
  padding: "2px 8px",
  borderRadius: 999,
  fontWeight: 600,
};

const inputStyle = {
  padding: 8,
  border: "1px solid #ccc",
  borderRadius: 4,
  fontSize: 13,
};

const btnPrimary = {
  background: "#1976d2",
  border: "1px solid #115293",
  color: "#fff",
  fontSize: 13,
  fontWeight: 600,
  padding: "8px 12px",
  borderRadius: 4,
  cursor: "pointer",
};

const btnSecondary = {
  background: "#eee",
  border: "1px solid #ccc",
  color: "#111",
  fontSize: 13,
  fontWeight: 600,
  padding: "8px 12px",
  borderRadius: 4,
  cursor: "pointer",
};

const miniBtn = {
  background: "#f3f3f3",
  border: "1px solid #ccc",
  color: "#111",
  fontSize: 12,
  fontWeight: 700,
  padding: "6px 10px",
  borderRadius: 6,
  cursor: "pointer",
};

const sectionTitleStyle = {
  fontSize: 16,
  fontWeight: 600,
  margin: 0,
  marginBottom: 12,
  color: "#222",
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const pillHeader = {
  fontSize: 12,
  fontWeight: 600,
  padding: "2px 8px",
  display: "inline-block",
  borderRadius: 999,
  border: "1px solid #ddd",
  background: "#f9f9f9",
  color: "#444",
  marginBottom: 8,
};

const thStyle = {
  textAlign: "left",
  padding: "8px 6px",
  borderBottom: "1px solid #eee",
  color: "#666",
  fontWeight: 700,
  fontSize: 12,
};

const tdStyle = {
  padding: "8px 6px",
  borderBottom: "1px solid #f2f2f2",
  verticalAlign: "top",
};

const emptyMini = {
  fontSize: 12,
  color: "#777",
  background: "#fff",
  border: "1px dashed #ccc",
  borderRadius: 4,
  padding: "8px 10px",
};
